import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Card,
  Table,
  Button,
  Badge,
  Row,
  Col,
  ProgressBar,
  Alert,
  Spinner,
  OverlayTrigger,
  Tooltip,
  Modal,
  Form,
} from "react-bootstrap";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  getFirestore,
  updateDoc,
  arrayUnion,
  query,
  where,
} from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaEdit,
  FaUsers,
  FaCheckCircle,
  FaClock,
  FaChartBar,
  FaClipboardList,
  FaUserPlus,
  FaEye,
  FaCalendarAlt,
  FaSearch,
} from "react-icons/fa";
import { BsCheckCircleFill, BsClockFill } from "react-icons/bs";
import firebaseApp from "../../firebase/credenciales";
import { logBusinessAction } from "../../services/AuditService";

const db = getFirestore(firebaseApp);

const formatearFecha = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(
    timestamp.seconds ? timestamp.seconds * 1000 : timestamp
  );
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const CampanaPage = ({ usuario }) => {
  const { campanaid } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [campana, setCampana] = useState(null);
  const [evaluados, setEvaluados] = useState([]);
  const [resultados, setResultados] = useState([]);
  const [showModalAsignar, setShowModalAsignar] = useState(false);
  const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [savingAssignments, setSavingAssignments] = useState(false);

  useEffect(() => {
    if (usuario?.ruc && campanaid) {
      cargarDatosCampana();
    }
  }, [usuario, campanaid]);

  const cargarDatosCampana = async () => {
    try {
      setLoading(true);
      const rucEmpresa = usuario?.ruc || "";

      // Cargar datos de la campaña
      const campanaRef = doc(
        db,
        `evaluaciones/${rucEmpresa}/campañas`,
        campanaid
      );
      const campanaSnap = await getDoc(campanaRef);

      if (!campanaSnap.exists()) {
        console.error("Campaña no encontrada");
        navigate("/evaluaciones");
        return;
      }

      const campanaData = {
        id: campanaSnap.id,
        ...campanaSnap.data(),
      };

      setCampana(campanaData);

      // Cargar datos de evaluados
      const evaluadosData = campanaData.evaluados || [];
      const evaluadosConDatos = [];

      for (let evaluado of evaluadosData) {
        const userRef = doc(db, "users", evaluado.dni);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          evaluadosConDatos.push({
            ...evaluado,
            name: userData.name || "",
            apellidos: userData.apellidos || "",
            email: userData.email || "",
            cargo: userData.cargo || "",
          });
        }
      }

      setEvaluados(evaluadosConDatos);

      // Cargar resultados
      const resultadosRef = collection(
        db,
        `evaluaciones/${rucEmpresa}/campañas/${campanaid}/resultados`
      );
      const resultadosSnap = await getDocs(resultadosRef);
      const resultadosData = [];

      resultadosSnap.forEach((doc) => {
        resultadosData.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setResultados(resultadosData);
    } catch (error) {
      console.error("Error al cargar datos:", error);
    } finally {
      setLoading(false);
    }
  };

  const abrirModalAsignar = async () => {
    try {
      const rucEmpresa = usuario?.ruc || "";
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("rucEmpresa", "==", rucEmpresa));
      const usersSnap = await getDocs(q);

      const usuarios = [];
      usersSnap.forEach((doc) => {
        const data = doc.data();
        // Excluir usuarios ya asignados
        const yaAsignado = (campana?.evaluados || []).some(
          (e) => e.dni === doc.id
        );
        if (!yaAsignado) {
          usuarios.push({
            dni: doc.id,
            name: data.name || "",
            apellidos: data.apellidos || "",
            email: data.email || "",
            cargo: data.cargo || "",
          });
        }
      });

      setUsuariosDisponibles(
        usuarios.sort((a, b) =>
          (a.apellidos || "").localeCompare(b.apellidos || "")
        )
      );
      setShowModalAsignar(true);
    } catch (error) {
      console.error("Error al cargar usuarios:", error);
    }
  };

  const handleSelectUser = (dni) => {
    setSelectedUsers((prev) =>
      prev.includes(dni) ? prev.filter((d) => d !== dni) : [...prev, dni]
    );
  };

  const asignarUsuarios = async () => {
    if (selectedUsers.length === 0) return;

    try {
      setSavingAssignments(true);
      const rucEmpresa = usuario?.ruc || "";
      const campanaRef = doc(
        db,
        `evaluaciones/${rucEmpresa}/campañas`,
        campanaid
      );

      const nuevosEvaluados = selectedUsers.map((dni) => ({
        dni: dni,
        estado: "pendiente",
        fechaAsignacion: new Date(),
      }));

      await updateDoc(campanaRef, {
        evaluados: arrayUnion(...nuevosEvaluados),
      });

      // AUDITORÍA ASIGNACIÓN EVALUADOS
      try {
        await logBusinessAction("ASIGNACION_EVALUADOS_CAMPANA", "GestionEvaluacionDesempeno", {
          campanaId: campanaid,
          campanaNombre: campana?.nombre,
          cantidadAsignados: selectedUsers.length,
          dnisAsignados: selectedUsers
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de asignación de evaluados", e);
      }

      setShowModalAsignar(false);
      setSelectedUsers([]);
      setSearchTerm("");
      await cargarDatosCampana();
    } catch (error) {
      console.error("Error al asignar usuarios:", error);
    } finally {
      setSavingAssignments(false);
    }
  };

  const usuariosFiltrados = usuariosDisponibles.filter(
    (u) =>
      u.dni.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.apellidos || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div
        className="min-vh-100 d-flex justify-content-center align-items-center"
        style={{
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <div className="text-center">
          <Spinner
            animation="border"
            variant="primary"
            style={{ width: "3rem", height: "3rem" }}
          />
          <p className="mt-3 fw-semibold">Cargando campaña...</p>
        </div>
      </div>
    );
  }

  if (!campana) {
    return (
      <div
        className="min-vh-100 d-flex justify-content-center align-items-center"
        style={{
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <Alert variant="danger">Campaña no encontrada</Alert>
      </div>
    );
  }

  const calcularEstado = () => {
    if (!campana.fechaInicio || !campana.fechaFin) return "BORRADOR";

    const ahora = new Date();
    const inicio = new Date(
      campana.fechaInicio.seconds
        ? campana.fechaInicio.seconds * 1000
        : campana.fechaInicio
    );
    const fin = new Date(
      campana.fechaFin.seconds ? campana.fechaFin.seconds * 1000 : campana.fechaFin
    );

    if (ahora < inicio) return "PROGRAMADA";
    if (ahora > fin) return "FINALIZADA";
    return "ACTIVA";
  };

  const estado = calcularEstado();
  const totalEvaluados = evaluados.length;
  const completados = evaluados.filter((e) => e.estado === "completado").length;
  const enProceso = evaluados.filter((e) => e.estado === "en_proceso").length;
  const pendientes = evaluados.filter((e) => e.estado === "pendiente").length;
  const porcentajeAvance = totalEvaluados > 0 ? (completados / totalEvaluados) * 100 : 0;

  const getTipoLabel = (tipo) => {
    const tipos = { "90": "90°", "180": "180°", "360": "360°" };
    return tipos[tipo] || tipo;
  };

  const getBadgeVariant = (estado) => {
    switch (estado?.toUpperCase()) {
      case "COMPLETADO":
        return "success";
      case "EN_PROCESO":
        return "warning";
      case "PENDIENTE":
        return "secondary";
      default:
        return "secondary";
    }
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        <Card
          className="shadow-lg border-0"
          style={{ borderRadius: "15px", overflow: "hidden" }}
        >
          {/* Header */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              borderBottom: "3px solid #ff8a80",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaClipboardList className="me-3" />
                {campana.nombre}
              </h2>
              <div className="d-flex justify-content-center gap-3 mt-2">
                <Badge bg="light" text="dark" style={{ fontSize: "0.9rem" }}>
                  {getTipoLabel(campana.tipoEvaluacion)}
                </Badge>
                <Badge
                  bg={estado === "ACTIVA" ? "success" : "info"}
                  style={{ fontSize: "0.9rem" }}
                >
                  {estado}
                </Badge>
              </div>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Botones de acción */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-3">
                <Row className="align-items-center">
                  <Col md={8}>
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-3 p-3 text-center"
                        style={{
                          background:
                            "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                          color: "white",
                        }}
                      >
                        <FaClipboardList size={24} />
                      </div>
                      <div>
                        <h5 className="mb-1 fw-semibold">Detalle de Campaña</h5>
                        <p className="text-muted mb-0 small">
                          <FaCalendarAlt className="me-1" />
                          {formatearFecha(campana.fechaInicio)} -{" "}
                          {formatearFecha(campana.fechaFin)}
                        </p>
                      </div>
                    </div>
                  </Col>
                  <Col md={4} className="text-end">
                    <div className="d-flex gap-2 justify-content-end">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Asignar evaluados</Tooltip>}
                      >
                        <Button
                          className="d-flex align-items-center gap-2 text-white"
                          onClick={abrirModalAsignar}
                          style={{
                            borderRadius: "10px",
                            background:
                              "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                            border: "none",
                          }}
                        >
                          <FaUserPlus size="16px" />
                          Asignar
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Editar campaña</Tooltip>}
                      >
                        <Button
                          variant="outline-secondary"
                          className="d-flex align-items-center gap-2"
                          onClick={() =>
                            navigate(`/evaluaciones/editar-campana/${campanaid}`)
                          }
                          style={{
                            borderRadius: "10px",
                            borderColor: "#f093fb",
                            color: "#f093fb",
                          }}
                        >
                          <FaEdit size="16px" />
                          Editar
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Ver resultados consolidados</Tooltip>}
                      >
                        <Button
                          className="d-flex align-items-center gap-2 text-white"
                          onClick={() =>
                            navigate(`/evaluaciones/resultados/${campanaid}`)
                          }
                          style={{
                            borderRadius: "10px",
                            background:
                              "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                            border: "none",
                          }}
                        >
                          <FaChartBar size="16px" />
                          Resultados
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Volver al dashboard</Tooltip>}
                      >
                        <Button
                          variant="outline-secondary"
                          className="d-flex align-items-center gap-2"
                          onClick={() => navigate("/evaluaciones")}
                          style={{ borderRadius: "10px" }}
                        >
                          <FaArrowLeft size="16px" />
                          Volver
                        </Button>
                      </OverlayTrigger>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Estadísticas */}
            <Row className="mb-4">
              <Col md={3}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background:
                        "linear-gradient(135deg, #17a2b8 0%, #20c997 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaUsers size={32} className="mb-2" />
                    <h4 className="fw-bold">{totalEvaluados}</h4>
                    <p className="mb-0">Total Evaluados</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background:
                        "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <BsCheckCircleFill size={32} className="mb-2" />
                    <h4 className="fw-bold">{completados}</h4>
                    <p className="mb-0">Completadas</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background:
                        "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <BsClockFill size={32} className="mb-2" />
                    <h4 className="fw-bold">{enProceso}</h4>
                    <p className="mb-0">En Proceso</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background:
                        "linear-gradient(135deg, #6c757d 0%, #495057 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaClock size={32} className="mb-2" />
                    <h4 className="fw-bold">{pendientes}</h4>
                    <p className="mb-0">Pendientes</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Progreso general */}
            <Card
              className="border-0 shadow-sm mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-4">
                <h5 className="fw-semibold mb-3">
                  <FaChartBar className="me-2" style={{ color: "#f093fb" }} />
                  Progreso General de la Campaña
                </h5>
                <div className="d-flex align-items-center gap-3">
                  <div className="flex-grow-1">
                    <ProgressBar
                      now={porcentajeAvance}
                      label={`${porcentajeAvance.toFixed(1)}%`}
                      style={{ height: "30px" }}
                      variant="success"
                    />
                  </div>
                  <div className="text-end" style={{ minWidth: "100px" }}>
                    <h5 className="mb-0 fw-bold" style={{ color: "#28a745" }}>
                      {completados}/{totalEvaluados}
                    </h5>
                    <small className="text-muted">Completados</small>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Descripción */}
            {campana.descripcion && (
              <Card
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Body className="p-4">
                  <h5 className="fw-semibold mb-3">Descripción</h5>
                  <p className="text-muted mb-0">{campana.descripcion}</p>
                </Card.Body>
              </Card>
            )}

            {/* Tabla de evaluados */}
            <Card
              className="border-0 shadow-sm mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Header
                className="text-white py-3"
                style={{
                  background:
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="mb-0 fw-semibold">
                    <FaUsers className="me-2" />
                    Lista de Evaluados
                  </h5>
                  <Badge bg="light" text="dark" style={{ fontSize: "0.9rem" }}>
                    {evaluados.length} personas
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {evaluados.length === 0 ? (
                  <div className="text-center py-5">
                    <FaUsers size={48} className="text-muted mb-3" />
                    <p className="text-muted">
                      No hay evaluados asignados a esta campaña
                    </p>
                    <Button
                      className="text-white"
                      onClick={abrirModalAsignar}
                      style={{
                        borderRadius: "10px",
                        background:
                          "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                        border: "none",
                      }}
                    >
                      <FaUserPlus className="me-2" />
                      Asignar Evaluados
                    </Button>
                  </div>
                ) : (
                  <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                    <Table hover responsive className="mb-0">
                      <thead
                        style={{
                          backgroundColor: "#f8f9fa",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                        }}
                      >
                        <tr>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">#</th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">DNI</th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">Nombre Completo</th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">Cargo</th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">Estado</th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {evaluados.map((evaluado, index) => (
                          <tr key={evaluado.dni}>
                            <td className="text-center py-3 px-3">
                              <Badge
                                style={{
                                  background:
                                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                }}
                              >
                                {index + 1}
                              </Badge>
                            </td>
                            <td className="py-3 px-3">
                              <span className="fw-semibold">{evaluado.dni}</span>
                            </td>
                            <td className="py-3 px-3">
                              <div>
                                <div className="fw-bold text-dark">
                                  {evaluado.apellidos}, {evaluado.name}
                                </div>
                                <small className="text-muted">{evaluado.email}</small>
                              </div>
                            </td>
                            <td className="py-3 px-3">
                              <span className="text-muted">{evaluado.cargo || "N/A"}</span>
                            </td>
                            <td className="text-center py-3 px-3">
                              <Badge
                                bg={getBadgeVariant(evaluado.estado)}
                                style={{ fontSize: "0.75rem" }}
                              >
                                {evaluado.estado?.toUpperCase() || "PENDIENTE"}
                              </Badge>
                            </td>
                            <td className="text-center py-3 px-3">
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Ver resultados</Tooltip>}
                              >
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() =>
                                    navigate(
                                      `/evaluaciones/resultados/${campanaid}`
                                    )
                                  }
                                  style={{
                                    borderRadius: "6px",
                                    borderColor: "#f093fb",
                                    color: "#f093fb",
                                  }}
                                >
                                  <FaEye size="12px" />
                                </Button>
                              </OverlayTrigger>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>
      </Container>

      {/* Modal para asignar usuarios */}
      <Modal
        show={showModalAsignar}
        onHide={() => setShowModalAsignar(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaUserPlus className="me-2" />
            Asignar Evaluados
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <div className="position-relative">
              <FaSearch
                className="position-absolute"
                style={{
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#6c757d",
                }}
              />
              <Form.Control
                type="text"
                placeholder="Buscar por DNI, nombre o apellidos..."
                className="ps-5"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ borderRadius: "10px" }}
              />
            </div>
          </Form.Group>

          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {usuariosFiltrados.length === 0 ? (
              <Alert variant="info">No hay usuarios disponibles</Alert>
            ) : (
              <Table hover>
                <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa" }}>
                  <tr>
                    <th></th>
                    <th>DNI</th>
                    <th>Nombre</th>
                    <th>Cargo</th>
                  </tr>
                </thead>
                <tbody>
                  {usuariosFiltrados.map((user) => (
                    <tr key={user.dni}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedUsers.includes(user.dni)}
                          onChange={() => handleSelectUser(user.dni)}
                        />
                      </td>
                      <td>{user.dni}</td>
                      <td>
                        {user.apellidos}, {user.name}
                      </td>
                      <td className="text-muted">{user.cargo || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          <Alert variant="info" className="mt-3 mb-0">
            <strong>{selectedUsers.length}</strong> usuario(s) seleccionado(s)
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowModalAsignar(false)}>
            Cancelar
          </Button>
          <Button
            className="text-white"
            onClick={asignarUsuarios}
            disabled={selectedUsers.length === 0 || savingAssignments}
            style={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              border: "none",
            }}
          >
            {savingAssignments ? "Asignando..." : `Asignar ${selectedUsers.length} usuario(s)`}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CampanaPage;
